<script lang="ts">
  import { ConfirmActions, Title, Loader, Alert } from '$shared/components';
  import FileList from './FileList.svelte';
  import Label from '$shared/constants/Label';
  import type { FileAttachPopupFormModel } from '$shared/models/forms/FileAttachPopupFormModel';
  import { AlertOption } from '$shared/models/AlertOption';
  import ModalEvent from '$shared/constants/ModalEvent';
  import type { Document } from '$shared/models/Document';

  interface Props {
    loading?: boolean;
    loadingFiles?: boolean;
    error?: string;
    desc: string;
    formData: FileAttachPopupFormModel;
    onevent?: (payload: any) => void;
  }

  let {
    loading = false,
    loadingFiles = false,
    error = '',
    desc,
    formData = $bindable(),
    onevent
  }: Props = $props();

  const handleOk = () => {
    onevent?.({ type: ModalEvent.ok, data: formData });
  };

  const handleCancel = () => {
    onevent?.({ type: ModalEvent.close });
  };

  const onClickErrorClose = () => {
    onevent?.({ type: 'error-close' });
  };

  const onClickOpenLink = (detail: Document): void => {
    onevent?.({ type: 'open-file', data: detail });
  };

  const onClickSelectFile = (doc: Document): void => {
    formData.upsertFile(doc.file);
    formData = formData;
  };
</script>

<div class="cvo-file-popup">
  <div data-cy="cvo-file-popup-title" class="title">
    <Title type="h2">Choose files</Title>
    <div data-cy="cvo-file-popup-desc" class="desc">
      {desc || 'Choose files from the documents. You can attach multiple files.'}
    </div>
  </div>
  <div class="file-list">
    {#if error}
      <div class="error-alert">
        <Alert
          closable
          maxWidth
          data={error}
          options={AlertOption.errorAlert()}
          onclose={onClickErrorClose}
        />
      </div>
    {/if}
    {#if loadingFiles}
      <div class="loader">
        <Loader count={3} />
      </div>
    {:else}
      <FileList
        title={formData.hasUnsupportedFiles ? 'Supported Files' : ''}
        docs={formData.supportedFiles}
        selectedItems={formData.selectedFileItems}
        onclick={onClickSelectFile}
        onopenfile={onClickOpenLink}
      />
      {#if formData.hasUnsupportedFiles}
        <FileList disabled title="Unsupported Files" docs={formData.unSupportedFiles} />
      {/if}
    {/if}
  </div>
  <div class="actions">
    <ConfirmActions
      {loading}
      okLabel={Label.Attach}
      disabled={loading || formData.noFilesSelected}
      onok={handleOk}
      oncancel={handleCancel}
    />
  </div>
</div>

<style>
  .cvo-file-popup {
    padding: 24px;
  }
  .error-alert {
    padding: 16px 16px 0;
  }
  .title {
    margin-bottom: 16px;
  }
  .loader {
    padding: 16px;
  }
  .desc {
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    margin-top: 8px;
    color: var(--grey-dark);
  }
  .file-list {
    width: 100%;
    max-height: calc(70vh - 200px);
    border: 1px solid #c6c9ce;
    box-shadow: 1px 2px 2px rgba(0, 0, 0, 0.02);
    border-radius: 3px;
    margin-top: 16px;
    margin-bottom: 24px;
    overflow: auto;
  }
</style>
